import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateGnrRequestMutation, useUpdateGnrRequestMutation } from '../../../../hooks/mutations/useGnrRequestMutation';
import { useGnrSuppliersQuery } from '../../../../hooks/queries/useGnrSuppliersQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { GnrRequest } from '../../../../services/apiSLTP/sites/gnrRequests';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getDateXDaysAfter, getTomorrowDate } from '../../../../utils/dates';
import { getGnrPeriodInfos, gnrPeriod, gnrState } from '../../../../utils/enums';
import CustomDateInput from '../../../CustomDateInput';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageGnrPlannedModalProps extends ModalProps {
    gnrRequest: GnrRequest | null;
    refetch: () => void;
}

const ManageGnrPlannedModal: React.FC<ManageGnrPlannedModalProps> = ({ gnrRequest, handleClose, open, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataGnrSuppliers, isLoading: isGnrSuppliersLoading } = useGnrSuppliersQuery({ queries: { active: true } });
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedGnrSupplier, setSelectedSupplier] = useState({
        label: gnrRequest?.gnrSupplierId ? `${gnrRequest?.gnrSupplierId?.name} - ${gnrRequest?.gnrSupplierId?.area}` : '',
        value: gnrRequest?.gnrSupplierId ? gnrRequest?.gnrSupplierId?._id : '',
    });
    const [selectedDate, setSelectedDate] = useState(
        gnrRequest?.plannedDate ? new Date(gnrRequest.plannedDate) : getTomorrowDate().getDay() === 6 ? getDateXDaysAfter(3) : getTomorrowDate()
    );
    const [selectedPeriod, setSelectedPeriod] = useState({
        label: capitalizeFirstLetter(t(getGnrPeriodInfos(gnrRequest?.plannedPeriod ? gnrRequest?.plannedPeriod : gnrPeriod.MORNING))),
        value: gnrRequest?.plannedPeriod ? gnrRequest?.plannedPeriod.toString() : gnrPeriod.MORNING.toString(),
    });
    const [selectedVolume, setSelectedVolume] = useState<string>(gnrRequest?.plannedVolume ? gnrRequest?.plannedVolume.toString() : '');

    const { mutate: mutateGnrRequest } = useUpdateGnrRequestMutation({
        onSettled: refetch,
        onError: (error) => console.error('Error updating gnrRequest:', error.message),
    });

    const { mutate: addGnrRequest } = useCreateGnrRequestMutation({
        onSettled: refetch,
        onError: (error) => console.error('Error adding gnrRequest:', error.message),
    });

    const handleSubmit = () => {
        if (gnrRequest) {
            mutateGnrRequest({
                gnrRequestId: gnrRequest?._id,
                data: {
                    plannedDate: selectedDate,
                    plannedPeriod: parseInt(selectedPeriod.value),
                    plannedVolume: parseFloat(selectedVolume.replace(',', '.')),
                    plannerId: user?._id,
                    gnrSupplierId: selectedGnrSupplier.value,
                },
            });
        }
    };

    useEffect(() => {
        if (gnrRequest && dataUsers) {
            if (!selectedUser) {
                const userFromRequest = dataUsers.find((u) => u._id === gnrRequest.plannerId?._id);
                if (userFromRequest) {
                    setSelectedUser({
                        value: userFromRequest._id,
                        label: formatName(userFromRequest.firstName, userFromRequest.lastName),
                    });
                }
            }
        }
    }, [gnrRequest, dataUsers]);

    return (
        <ModalBody
            title={t('gnrPlanned')}
            subTitle={t('update')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            disabledOk={!(selectedVolume?.length && selectedPeriod.value.length && selectedGnrSupplier.value.length)}
            handleOk={handleSubmit}
            thirdButton={[gnrState.REQUESTED, gnrState.IN_PROGRESS].includes(gnrRequest?.state || -1)}
            thirdText={t('delete')}
            handleThird={() => mutateGnrRequest({ gnrRequestId: gnrRequest?._id || '', data: { state: gnrState.CANCELED } })}
        >
            <>
                <CustomTextInput
                    value={selectedVolume.toString()}
                    label={t('volume')}
                    onChange={(text) => setSelectedVolume(text)}
                    errorText={t('error')}
                    type="number"
                    required
                />
                <CustomDateInput
                    label={t('deliveredDate')}
                    onChange={(value) => (value ? setSelectedDate(new Date(value?.toISOString())) : null)}
                    required
                    minDate={new Date()}
                    defaultDate={selectedDate}
                />
                <DropDown
                    selectedItem={selectedPeriod}
                    items={
                        Object.values(gnrPeriod).map((period) => ({
                            label: capitalizeFirstLetter(t(getGnrPeriodInfos(period))),
                            value: period.toString(),
                        })) || []
                    }
                    handleChange={(event, value) => setSelectedPeriod({ value: value?.value || '', label: value?.label || '' })}
                    inputLabel={t('period')}
                    sortItems={false}
                    required
                />
                <DropDown
                    items={
                        dataGnrSuppliers?.data.map((gnrSupplier) => ({
                            label: gnrSupplier.name + ' - ' + gnrSupplier.area,
                            value: gnrSupplier._id,
                        })) || []
                    }
                    selectedItem={selectedGnrSupplier}
                    handleChange={(e, v) => setSelectedSupplier(v || { label: '', value: '' })}
                    inputLabel={t('gnrSupplier')}
                    required
                    loading={isGnrSuppliersLoading}
                />
                <DropDown
                    selectedItem={selectedUser}
                    items={
                        dataUsers?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                    }
                    handleChange={(event, value) => setSelectedUser(value)}
                    inputLabel={t('person')}
                    loading={isUsersLoading}
                />
            </>
        </ModalBody>
    );
};

export default ManageGnrPlannedModal;
